'use client'

import { useCallback, useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { legalApi, type EulaDocument } from '@/lib/api/legal-client'

/**
 * Blocking clickwrap gate, mounted app-wide in Providers. Any signed-in
 * user who has not accepted the CURRENT EULA text (hash-keyed — any edit
 * to backend/legal/EULA.md re-triggers it) gets a full-screen review &
 * agree dialog. This single mechanism covers new users right after their
 * first sign-in, existing users at rollout, and everyone after each
 * update to the agreement.
 */

// Pages a signed-out (or not-yet-agreed) visitor must still be able to
// read — auth flows, the agreement itself, and public info pages.
const PUBLIC_PREFIXES = ['/login', '/register', '/auth', '/legal', '/about', '/docs', '/support']

export default function EulaGate() {
  const { isAuthenticated, isLoading, logout } = useAuth()
  const pathname = usePathname()
  const [doc, setDoc] = useState<EulaDocument | null>(null)
  const [open, setOpen] = useState(false)
  const [agreeChecked, setAgreeChecked] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isPublic = PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'))

  const check = useCallback(async () => {
    try {
      const status = await legalApi.getStatus()
      if (status.accepted) {
        setOpen(false)
        return
      }
      const document = await legalApi.getEula()
      setDoc(document)
      setAgreeChecked(false)
      setOpen(true)
    } catch {
      // Best-effort: if the backend is unreachable or the EULA file is
      // missing (503), don't lock users out of the whole app.
      setOpen(false)
    }
  }, [])

  useEffect(() => {
    if (isLoading || !isAuthenticated || isPublic) return
    check()
    // Re-checking on every route change is one cheap indexed query and
    // catches an EULA update without requiring a full page reload.
  }, [isLoading, isAuthenticated, isPublic, pathname, check])

  if (!open || isPublic || !isAuthenticated || !doc) return null

  const handleAgree = async () => {
    setSubmitting(true)
    setError(null)
    try {
      await legalApi.accept(doc.sha256)
      setOpen(false)
    } catch (err) {
      // Typically 409: the document changed while the dialog was open.
      setError(err instanceof Error ? err.message : 'Failed to record acceptance')
      await check()
    } finally {
      setSubmitting(false)
    }
  }

  const handleDecline = async () => {
    setSubmitting(true)
    try {
      await logout()
    } finally {
      setSubmitting(false)
      setOpen(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="End-User License Agreement"
    >
      <div className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-panel-md">
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-800">End-User License Agreement</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Version {doc.version} — the agreement (or an update to it) requires your review.
            Please read and agree to continue using Designer Space.
          </p>
        </div>

        <div className="md min-h-0 flex-1 overflow-y-auto break-words px-6 py-4">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{ a: (props) => <a {...props} target="_blank" rel="noopener noreferrer" /> }}
          >
            {doc.content}
          </ReactMarkdown>
        </div>

        <div className="space-y-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
          {error && <p className="text-xs text-red-600">{error}</p>}
          <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-secondary"
              checked={agreeChecked}
              onChange={(e) => setAgreeChecked(e.target.checked)}
              disabled={submitting}
            />
            <span>I have read and agree to the End-User License Agreement</span>
          </label>
          <div className="flex justify-end gap-2">
            <button
              onClick={handleDecline}
              disabled={submitting}
              className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-50"
            >
              Decline &amp; sign out
            </button>
            <button
              onClick={handleAgree}
              disabled={!agreeChecked || submitting}
              className="inline-flex items-center rounded-md bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:pointer-events-none disabled:opacity-50"
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              I Agree
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
